import { t } from '!/i18n'
import { model as router } from '!/router'
import { lookbehind } from '@/effector'
import { logger } from '@/logger'
import { api } from '@setplex/tria-api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { debounce } from 'patronum/debounce'
import { AuthTab, model as tabsModel } from '~/features/change-auth-type'
import {
  signInByEmailModel,
  signInByPhoneModel,
  signInByTrueIdFeatureModel,
  model as signInModel,
  type ViewError,
} from '~/features/subscriber/sign-in'
import { token } from '~/routes/auth/shared/tools'
import { aye } from '~/shared/helpers'
import { formatDateDuration } from '~/shared/helpers/date'
import { stopTimerError, updateTextError } from '../shared/model'

// init page
export const init = createEvent()
export const left = createEvent()

// effect to create hash token
const tokenFx = createEffect<
  { id: string; time: number; unit: string },
  string
>(({ id, time, unit }) => token(id, time, unit))

// in case we could not encode token -> log error
sample({
  clock: tokenFx.fail,
  fn: (fail) => ['could not encode token:', fail],
  target: logger.errorFx,
})

// redirect to verify page
sample({
  clock: tokenFx.doneData,
  source: signInByPhoneModel.signInByPhoneFx.done,
  fn: ({ params }, token) => ({
    pathname: `/sign-in/verify?=${AuthTab.Phone}`,
    hash: token,
    state: params,
  }),
  target: router.navigatePush,
})

// error to show to the user
export const $error = createStore<ViewError>(null)
export const onError = createEvent<ViewError | null>()
// send sign-in request
sample({
  clock: signInByEmailModel.signInForm.formValidated,
  source: { rememberMe: signInModel.$rememberMe },
  fn: ({ rememberMe }, { username, password }) => ({
    username: username.trim(),
    password,
    rememberMe,
  }),
  target: signInModel.signIn,
})

sample({
  clock: signInByPhoneModel.signInByPhoneFx.doneData,
  fn: ({ id, time_value, time_period }) => ({
    id,
    time: time_value,
    unit: time_period,
  }),
  target: tokenFx,
})

sample({
  clock: init,
  target: [
    signInByEmailModel.signInForm.reset,
    signInByPhoneModel.signInByPhoneForm.reset,
  ],
})

//
// handle dynamic fields errors (~ server side validation)
//

// WG0022 - Email is not unique
sample({
  clock: api.events.logic.emailNotUnique,
  fn: () => ({ rule: 'unique' }),
  target: signInByEmailModel.signInForm.fields.username.addError,
})

//
// add listeners for server errors
//

const initNot401 = lookbehind({
  source: init,
  clear: api.events.http.unauthorized,
  within: 100,
})

$error
  .reset(
    signInByEmailModel.signInForm.$values.updates,
    initNot401,

    // "server validation" errors
    api.events.logic.emailNotUnique,
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.apiBlocked, // EC300 | WG0019
    () => 'provider_is_blocked_error'
  )
  .on(
    api.events.logic.authNotAllowedIPAddress, // EC50 | WG0017
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.badCredentials, // EC10 | WG0008
    () => 'password_or_username_invalid_error'
  )
  .on(
    api.events.logic.countryNotValid, // WG0031
    () => 'invalid_country_error'
  )
  .on(
    api.events.logic.deviceTypeNotAllowed, // EC21 | WG0016
    () => 'web_app_disabled_error'
  )
  .on(
    api.events.logic.invalidReferer, // WG0021
    () => 'domain_disabled'
  )
  .on(
    api.events.logic.maxNumberDevicesAssigned, // EC06 | WG0006
    () => 'max_devices_assigned_error'
  )
  .on(
    api.events.logic.serverUnderMaintenance, // 503
    () => 'server_maintenance_error'
  )
  .on(
    api.events.logic.subscriptionNotActive, // WG0023
    () => 'subscription_expired_error'
  )
  .on(
    api.events.logic.unsupportedPlatform, // WG0035
    () => 'app_not_supported_error'
  )
  .on(
    api.events.logic.webDeviceInitError, // WG0020
    () => 'failed_init_app_error'
  )
  .on(
    api.events.logic.webDeviceNotFound, // EC22
    () => 'failed_open_app_error'
  )
  .on(
    api.events.logic.yourIPAddressLocked, // EC55 | WG0018
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.internalServerError, // 500 | WB0500 | WG0500
    () => 'unknown_error'
  )
  .on(
    api.events.logic.signInByPhoneDisabled, // WG0094
    () => 'phone_number_sign_in_disabled_error'
  )
  .on(
    api.events.logic.tooManyCodeRequests, // WG0094
    () => 'code_request_attempt_error'
  )
  // TODO: It is necessary to reconsider the approach to the implementation of error translation with additional options
  .on(
    api.events.logic.noMoreVerifyAttempts, // WG0099
    (_, { payload }) => {
      const time = formatDateDuration({
        duration: {
          seconds: payload?.remainsSeconds,
        },
      })
      return t('invalid_sms_code_attempt_with_time_error', {
        time,
      })
    }
  )
  .on(
    api.events.logic.phoneNumberNotValid, // WG0027
    () => 'invalid_phone_number_error'
  )
  .on(
    api.events.logic.subscriptionHasBeenExpired, // WG0009
    () => 'subscription_expired_error'
  )
  .on(
    api.events.logic.accountOrDeviceHasBeenSuspended, // WG0015
    () => 'account_or_device_suspended_error'
  )
  // some generic request errors
  .on(
    api.events.http.unauthorized, // 401
    (error, data) => {
      const isGuest401 = data?.request?.url?.includes('/guest')

      return isGuest401 ? error : 'session_expired_error'
    }
  )
  .on(
    api.events.http.httpError, // any non-200 error
    (error, { request, response }) => {
      const isGuest401 =
        request?.url?.includes('/guest') && response?.status === 401

      if (isGuest401 || error !== null) return error

      return 'unknown_error'
    }
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: onError,
})
sample({
  source: onError,
  filter: (error) => aye(error),
  target: signInModel.$viewError,
})

sample({
  clock: signInModel.$signInWithPhoneEnabled,
  fn: (phoneEnabled) => (phoneEnabled ? AuthTab.Phone : AuthTab.Email),
  target: tabsModel.$selectedTab,
})

sample({
  clock: updateTextError,
  fn: (data) => data.translated,
  target: signInModel.$viewError,
})

sample({
  clock: [signInByEmailModel.signInForm.$values.updates, initNot401, init],
  target: stopTimerError,
})

signInModel.$viewError.reset(
  signInByEmailModel.signInForm.$values.updates,
  initNot401
)

signInByTrueIdFeatureModel.$trueIdError.reset(left)
tabsModel.$selectedTab.reset(init)
